import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, ChevronRight, Tag, Clock, Search, Newspaper, RefreshCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import Breadcrumbs from '../components/Breadcrumbs';
import useTenantGeoRedirect from '../hooks/useTenantGeoRedirect';
// Use centralized API service
import { municipiosApi, noticiasApi } from '../services/api';
import SEOHead from '../components/SEOHead';
import { resolveBackendImage } from '../utils/brandLogo';

/**
 * Sanitiza conteúdo de notícias colado de Word/Google Docs. Editores externos
 * inserem `&nbsp;` (U+00A0) entre TODAS as palavras — o navegador não pode
 * quebrar linha em espaços não-quebráveis, e o texto vaza à direita.
 * Também remove sequências dos entities correspondentes por segurança.
 */
function sanitizeNewsHtml(html) {
  if (!html) return '';
  return html
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/\u00A0/g, ' ')
    // colapsa múltiplos espaços para 1 (evita "espaços gigantes")
    .replace(/ {2,}/g, ' ');
}

function NewsCard({ news, featured = false }) {
  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Recente';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (featured) {
    return (
      <article
        className="bg-gradient-to-r from-[#56007F] to-[#7B2FA8] rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-shadow"
        onClick={() => navigate(`/noticias/${news.slug || news.id}`)}
        data-testid={`featured-news-${news.id}`}
      >
        <div className="flex flex-col md:flex-row">
          {news.capa_url && (
            <div className="md:w-2/5 h-48 md:h-auto">
              <img 
                src={resolveBackendImage(news.capa_url)} 
                alt={news.titulo} 
                className="w-full h-full object-cover"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
          <div className="flex-1 p-8 text-white">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {news.tags?.slice(0, 2).map(tag => (
                <span key={tag} className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  {tag}
                </span>
              ))}
              <span className="text-white/70 text-sm flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(news.publicado_em)}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              {news.titulo}
            </h2>
            <p className="text-white/90 text-lg mb-6 leading-relaxed line-clamp-3">
              {news.resumo}
            </p>
            <button className="inline-flex items-center gap-2 bg-white text-[var(--primary)] hover:bg-gray-100 rounded-full px-6 py-2.5 font-semibold transition-colors">
              Ler mais
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-shadow group"
      onClick={() => navigate(`/noticias/${news.slug || news.id}`)}
      data-testid={`news-card-${news.id}`}
    >
      {news.capa_url && (
        <div className="h-40 bg-gray-100 overflow-hidden">
          <img 
            src={resolveBackendImage(news.capa_url)} 
            alt={news.titulo} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {news.tags?.slice(0, 1).map(tag => (
            <span key={tag} className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              {tag}
            </span>
          ))}
          <span className="text-gray-400 text-xs flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDate(news.publicado_em)}
          </span>
        </div>
        <h3 className="text-lg font-bold mb-2 group-hover:text-[var(--primary)] transition-colors line-clamp-2">
          {news.titulo}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2 mb-4">
          {news.resumo}
        </p>
        <span className="text-[var(--primary)] text-sm font-medium inline-flex items-center gap-1 hover:underline">
          Ler mais
          <ChevronRight className="w-4 h-4" />
        </span>
      </div>
    </article>
  );
}

export default function Noticias() {
  const navigate = useNavigate();
  const { slug, municipioSlug } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [noticias, setNoticias] = useState([]);
  const [noticiaDetalhe, setNoticiaDetalhe] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag] = useState('Todas');
  const [allTags, setAllTags] = useState(['Todas']);
  const [municipio, setMunicipio] = useState(null);

  // Auto-redirect /noticias → /<slug>/noticias. Skip when viewing a single
  // article (slug param present) since article URLs already contain the
  // município context implicitly.
  useTenantGeoRedirect({ routeName: 'noticias', currentSlug: municipioSlug || slug });

  // Load municipality info
  useEffect(() => {
    const loadMunicipio = async () => {
      if (municipioSlug) {
        try {
          const allMunicipios = await municipiosApi.list();
          const found = allMunicipios.find(m => m.slug === municipioSlug);
          if (found) setMunicipio(found);
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }
    };
    loadMunicipio();
  }, [municipioSlug]);

  useEffect(() => {
    if (slug) {
      fetchNoticiaDetalhe();
    } else {
      fetchNoticias();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchNoticias = async () => {
    try {
      setLoading(true);
      const response = await noticiasApi.list({ municipio_slug: municipioSlug });
      const data = response.noticias || [];
      setNoticias(data);
      
      // Extract unique tags
      const tags = new Set(['Todas']);
      data.forEach(n => n.tags?.forEach(t => tags.add(t)));
      setAllTags([...tags]);
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNoticiaDetalhe = async () => {
    try {
      setLoading(true);
      const response = await noticiasApi.get(slug);
      setNoticiaDetalhe(response);
    } catch (error) {
      console.error('Erro ao carregar notícia:', error);
      navigate('/noticias');
    } finally {
      setLoading(false);
    }
  };

  // Render detail page if slug is present
  if (slug && noticiaDetalhe) {
    return (
      <div className="min-h-screen bg-[var(--background)]">
        {/* Header with Breadcrumbs */}
        <div className="bg-gradient-to-r from-[var(--primary)] to-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <Breadcrumbs 
              items={[
                { label: 'Notícias', path: '/noticias' },
                { label: noticiaDetalhe.titulo, path: null }
              ]}
              municipio={municipio}
              className="mb-4"
            />
            
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {noticiaDetalhe.tags?.map(tag => (
                <span key={tag} className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  {tag}
                </span>
              ))}
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>
              {noticiaDetalhe.titulo}
            </h1>
            
            <div className="flex items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {noticiaDetalhe.publicado_em 
                  ? new Date(noticiaDetalhe.publicado_em).toLocaleDateString('pt-BR', {
                      day: '2-digit', month: 'long', year: 'numeric'
                    })
                  : 'Recente'
                }
              </span>
              {noticiaDetalhe.autor && (
                <span>Por: {noticiaDetalhe.autor}</span>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {noticiaDetalhe.capa_url && (
            <div className="mb-8 rounded-2xl overflow-hidden">
              <img 
                src={resolveBackendImage(noticiaDetalhe.capa_url)} 
                alt={noticiaDetalhe.titulo}
                className="w-full h-auto max-h-96 object-cover"
              />
            </div>
          )}
          
          {noticiaDetalhe.resumo && (
            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-l-4 border-[var(--primary)] pl-4">
              {noticiaDetalhe.resumo}
            </p>
          )}
          
          <article className="prose prose-lg max-w-none [text-wrap:pretty]">
            {noticiaDetalhe.conteudo?.trim().startsWith('<') ? (
              <div
                data-testid="noticia-html"
                dangerouslySetInnerHTML={{ __html: sanitizeNewsHtml(noticiaDetalhe.conteudo) }}
              />
            ) : (
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {sanitizeNewsHtml(noticiaDetalhe.conteudo)}
              </ReactMarkdown>
            )}
          </article>
          
          {/* Share / Back */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => navigate('/noticias')}
              className="flex items-center gap-2 text-[var(--primary)] hover:underline"
            >
              <ArrowLeft className="w-5 h-5" />
              Ver mais notícias
            </button>
          </div>
        </div>
        
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  // Filter news
  const featuredNews = noticias.find(n => n.destaque);
  const filteredNews = noticias.filter(news => {
    const matchesSearch = news.titulo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          news.resumo?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = activeTag === 'Todas' || news.tags?.includes(activeTag);
    return matchesSearch && matchesTag && news.id !== featuredNews?.id;
  });

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <SEOHead
        title={municipio ? `Notícias de ${municipio.nome}` : 'Notícias'}
        description={municipio
          ? `Últimas notícias e novidades de ${municipio.nome}. Fique por dentro do que acontece na comunidade empreendedora.`
          : 'Últimas notícias do Empreende Comunidade. Fique por dentro das novidades.'
        }
        canonical={municipioSlug ? `/${municipioSlug}/noticias` : '/noticias'}
      />
      {/* Header with Breadcrumbs */}
      <div className="bg-gradient-to-r from-[var(--primary)] to-purple-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Breadcrumbs 
            items={[
              { label: 'Notícias', path: null }
            ]}
            municipio={municipio}
            className="mb-4"
          />
          <div className="flex items-center gap-3">
            <Newspaper className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }} data-testid="noticias-page-title">
                Notícias
              </h1>
              <p className="text-white/80">
                Fique por dentro das novidades do Empreende Comunidade
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Featured News */}
        {featuredNews && (
          <div className="mb-10">
            <NewsCard news={featuredNews} featured={true} />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar notícias..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
              data-testid="search-news"
            />
          </div>

          {/* Tag Filter */}
          {allTags.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
              {allTags.slice(0, 6).map(tag => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTag === tag
                      ? 'bg-[var(--primary)] text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-[var(--primary)]'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* News Grid */}
        {filteredNews.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNews.map(news => (
              <NewsCard key={news.id} news={news} />
            ))}
          </div>
        ) : noticias.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Nenhuma notícia publicada ainda</p>
            <button
              onClick={() => navigate('/')}
              className="text-[var(--primary)] hover:underline"
            >
              Voltar para a home
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma notícia encontrada para sua busca</p>
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold mb-2">Receba nossas novidades</h3>
          <p className="text-gray-600 mb-6">
            Cadastre-se para receber as últimas notícias e dicas para empreendedores
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Seu e-mail"
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
            />
            <button className="bg-[var(--primary)] text-white hover:bg-[#450066] rounded-xl px-6 py-3 font-semibold transition-colors">
              Inscrever
            </button>
          </div>
        </div>
      </div>
      
    </div>
  );
}
